// ProfileEditModal — single modal that exposes EVERY intake-form field
// for an existing person, plus the address/contact/sex fields that the
// PetitionerDetailsModal collects. The point: one button on the
// PersonPage opens one form, no hunting between two modals.
//
// What it edits, all optional:
//   Identity:    first / last name, DOB, middle, suffix
//   Identifiers: NJ DL or state ID, SSN, ZIP
//   Contact:     street, city, state (default NJ), phone, email, sex
//
// On save → PATCH /api/jobs/:id/profile, which updates the jobs row +
// merges the contact fields into petitioner_details (preserving
// expungement-only fields like sbiNumber that this modal doesn't
// touch). When name/DOB changed, the response carries
// `identityChanged: true` and we offer a "Re-run scrapes" follow-up
// using the existing /retry endpoint — that's the most common reason
// someone edits a profile after intake (Damon's wrong-DOB case).

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { patchJobProfile, retryJob, type ProfilePatchInput } from "../api";
import { toast } from "./Toaster";
import type { JobRow } from "../types";

interface Props {
  job: JobRow;
  /** Existing petitioner_details for prefill. We POST the modal back
   *  with the full set of contact fields so a no-op edit doesn't
   *  inadvertently clear anything. */
  initialDetails: {
    streetAddress?: string;
    city?: string;
    state?: string;
    phone?: string;
    email?: string;
    sex?: "M" | "F" | "X";
  } | null;
  onClose: () => void;
  onSaved: (updatedJob: JobRow) => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  middleName: string;
  suffix: string;
  licenseNumber: string;
  ssn: string;
  zipCode: string;
  streetAddress: string;
  city: string;
  state: string;
  phone: string;
  email: string;
  sex: "" | "M" | "F" | "X";
}

function buildInitialState(job: JobRow, details: Props["initialDetails"]): FormState {
  return {
    firstName: job.first_name ?? "",
    lastName: job.last_name ?? "",
    dateOfBirth: job.date_of_birth ?? "",
    middleName: job.middle_name ?? "",
    suffix: job.suffix ?? "",
    licenseNumber: job.license_number ?? "",
    ssn: job.ssn ?? "",
    zipCode: job.zip_code ?? "",
    streetAddress: details?.streetAddress ?? "",
    city: details?.city ?? "",
    state: details?.state ?? "NJ",
    phone: details?.phone ?? "",
    email: details?.email ?? "",
    sex: details?.sex ?? "",
  };
}

export function ProfileEditModal({ job, initialDetails, onClose, onSaved }: Props): JSX.Element {
  const [form, setForm] = useState<FormState>(() => buildInitialState(job, initialDetails));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After save: if name/DOB changed, ask the user whether to re-run.
  // We DON'T re-run silently — for a real intake the operator knows;
  // for a typo correction they want to re-run. Either is one click.
  const [postSavePrompt, setPostSavePrompt] = useState<{
    updated: JobRow;
  } | null>(null);

  // Esc to close; click on the backdrop to close. Same UX as the other
  // modals on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const set = (patch: Partial<FormState>): void => {
    setForm((f) => ({ ...f, ...patch }));
    if (error) setError(null);
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    if (!form.firstName.trim() || !form.lastName.trim() || !form.dateOfBirth.trim()) {
      setError("First name, last name, and date of birth are required.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dateOfBirth)) {
      setError(`Date of birth must be YYYY-MM-DD (got "${form.dateOfBirth}").`);
      return;
    }
    setSaving(true);
    // Send only the fields that differ from initial state — keeps the
    // server's COALESCE logic clean and means a no-op save won't
    // accidentally clear an unset optional.
    //
    // We trim BEFORE diffing so a stray leading/trailing space (often
    // pasted accidentally) doesn't read as "this changed", and we send
    // the trimmed value rather than the raw one — never round-trip
    // whitespace into the database. Internal whitespace ("Mary Jane")
    // is preserved.
    const patch: ProfilePatchInput = {};
    const initial = buildInitialState(job, initialDetails);
    const trimmed = (s: string): string => s.trim();
    const diff = (key: keyof FormState, target: keyof ProfilePatchInput): void => {
      const next = trimmed(String(form[key] ?? ""));
      const prev = trimmed(String(initial[key] ?? ""));
      if (next !== prev) {
        // Empty string → null so the server clears the field.
        // Non-empty → the trimmed value.
        (patch as Record<string, string | null>)[target as string] = next === "" ? null : next;
      }
    };
    diff("firstName", "firstName");
    diff("lastName", "lastName");
    diff("dateOfBirth", "dateOfBirth");
    diff("middleName", "middleName");
    diff("suffix", "suffix");
    diff("licenseNumber", "licenseNumber");
    diff("ssn", "ssn");
    diff("zipCode", "zipCode");
    diff("streetAddress", "streetAddress");
    diff("city", "city");
    diff("state", "state");
    diff("phone", "phone");
    diff("email", "email");
    if (form.sex !== initial.sex) {
      patch.sex = form.sex === "" ? null : form.sex;
    }
    try {
      const res = await patchJobProfile(job.id, patch);
      if (res.identityChanged) {
        setPostSavePrompt({ updated: res.job });
      } else {
        toast(`Saved ${res.job.first_name} ${res.job.last_name}`, "success");
        onSaved(res.job);
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRerun = async (rerun: boolean): Promise<void> => {
    if (!postSavePrompt) return;
    if (!rerun) {
      toast("Saved", "success");
      onSaved(postSavePrompt.updated);
      onClose();
      return;
    }
    try {
      const updated = await retryJob(job.id);
      toast(`Saved + re-queued ${updated.first_name} ${updated.last_name}`, "success");
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Render via portal directly into document.body — same fix the
  // FloatingDock applies. PersonPage's <main className="fade-in"> has
  // a CSS animation on `transform` whose final keyframe leaves
  // `transform: matrix(1,0,0,1,0,0)` computed on the element. That
  // identity transform STILL creates a containing block for any
  // descendant `position: fixed`, so the modal's `inset: 0` was being
  // resolved against <main>'s 10,000-px-tall scrollable region instead
  // of the viewport — leaving the modal positioned far below the fold
  // and looking like the click did nothing. Portaling into <body>
  // sidesteps the entire ancestor chain.
  return createPortal(
    <>
      <div
        className="modal-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Edit profile"
        className="modal-card"
      >
        <header className="modal-hero">
          <div className="modal-hero-eyebrow">Profile</div>
          <h2 className="modal-hero-title">Edit Profile</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            className="modal-hero-close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="modal-body">

        {/* If we're between save and the re-run prompt, swap the form
            for a focused confirmation panel. Keeps the state of the
            form intact in case the user backs out. */}
        {postSavePrompt ? (
          <PostSavePanel
            updated={postSavePrompt.updated}
            onChoice={handleRerun}
          />
        ) : (
          <>
            <div className="text-[13px] mb-5" style={{ color: "var(--color-text-muted)", lineHeight: 1.5 }}>
              All fields optional except name + DOB. Changing name or DOB will
              prompt to re-run the court searches.
            </div>

            {/* Identity */}
            <FieldGroup label="Identity">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="First name" required>
                  <input className="input" value={form.firstName}
                    onChange={(e) => set({ firstName: e.target.value })} />
                </Field>
                <Field label="Last name" required>
                  <input className="input" value={form.lastName}
                    onChange={(e) => set({ lastName: e.target.value })} />
                </Field>
                <Field label="Date of birth (YYYY-MM-DD)" required>
                  <input className="input mono" value={form.dateOfBirth}
                    placeholder="1985-04-12"
                    onChange={(e) => set({ dateOfBirth: e.target.value })} />
                </Field>
                <Field label="Middle name">
                  <input className="input" value={form.middleName}
                    onChange={(e) => set({ middleName: e.target.value })} />
                </Field>
                <Field label="Suffix">
                  <input className="input" placeholder="Jr / Sr / III"
                    value={form.suffix}
                    onChange={(e) => set({ suffix: e.target.value })} />
                </Field>
                <Field label="Sex">
                  <select className="input"
                    value={form.sex}
                    onChange={(e) => set({ sex: (e.target.value as "" | "M" | "F" | "X") })}
                  >
                    <option value="">— optional —</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="X">Other / unspecified</option>
                  </select>
                </Field>
              </div>
            </FieldGroup>

            {/* Identifiers */}
            <FieldGroup label="Identifiers">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Driver's license / ID #">
                  <input className="input mono" value={form.licenseNumber}
                    onChange={(e) => set({ licenseNumber: e.target.value })} />
                </Field>
                <Field label="SSN">
                  <input className="input mono" placeholder="XXX-XX-XXXX"
                    value={form.ssn}
                    onChange={(e) => set({ ssn: e.target.value })} />
                </Field>
                <Field label="ZIP code">
                  <input className="input mono" maxLength={5}
                    value={form.zipCode}
                    onChange={(e) => set({ zipCode: e.target.value })} />
                </Field>
              </div>
            </FieldGroup>

            {/* Contact */}
            <FieldGroup label="Address & contact">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Street address" wide>
                  <input className="input" value={form.streetAddress}
                    onChange={(e) => set({ streetAddress: e.target.value })} />
                </Field>
                <Field label="City">
                  <input className="input" value={form.city}
                    onChange={(e) => set({ city: e.target.value })} />
                </Field>
                <Field label="State">
                  <input className="input mono" maxLength={2}
                    value={form.state}
                    onChange={(e) => set({ state: e.target.value.toUpperCase() })} />
                </Field>
                <Field label="ZIP">
                  <input className="input mono" maxLength={5}
                    value={form.zipCode}
                    onChange={(e) => set({ zipCode: e.target.value })} />
                </Field>
                <Field label="Phone">
                  <input className="input" value={form.phone}
                    onChange={(e) => set({ phone: e.target.value })} />
                </Field>
                <Field label="Email" wide>
                  <input className="input" type="email" value={form.email}
                    onChange={(e) => set({ email: e.target.value })} />
                </Field>
              </div>
            </FieldGroup>

            {error && (
              <div
                className="mt-4 rounded p-3 text-[13px]"
                style={{
                  background: "color-mix(in oklab, var(--color-rose-hi) 12%, transparent)",
                  border: "1px solid color-mix(in oklab, var(--color-rose-hi) 35%, transparent)",
                  color: "var(--color-rose-hi)",
                }}
              >
                {error}
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="text-[12px]"
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--color-text-muted)",
                  letterSpacing: "0.1em",
                  background: "transparent",
                  border: "1px solid var(--color-line)",
                  padding: "8px 16px",
                  borderRadius: 6,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="text-[12px] font-semibold"
                style={{
                  fontFamily: "var(--font-mono)",
                  background: "var(--color-text)",
                  color: "var(--color-bg)",
                  letterSpacing: "0.1em",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: 6,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "SAVING…" : "SAVE"}
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </>,
    document.body,
  );
}

function PostSavePanel({
  updated,
  onChoice,
}: {
  updated: JobRow;
  onChoice: (rerun: boolean) => void | Promise<void>;
}): JSX.Element {
  return (
    <div className="py-2">
      <div
        className="text-[14px] font-semibold mb-2"
        style={{ color: "var(--color-text)" }}
      >
        Profile updated.
      </div>
      <div
        className="text-[13px] mb-4"
        style={{ color: "var(--color-text-muted)", lineHeight: 1.5 }}
      >
        Name or DOB changed for <strong>{updated.first_name} {updated.last_name}</strong>{" "}
        (<span className="mono">DOB {updated.date_of_birth}</span>). The previously
        captured records were searched under the old inputs, they may not match the
        new identity. Re-run the court searches?
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChoice(false)}
          className="text-[12px]"
          style={{
            fontFamily: "var(--font-mono)",
            color: "var(--color-text-muted)",
            letterSpacing: "0.1em",
            background: "transparent",
            border: "1px solid var(--color-line)",
            padding: "8px 16px",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          KEEP EXISTING RECORDS
        </button>
        <button
          type="button"
          onClick={() => onChoice(true)}
          className="text-[12px] font-semibold"
          style={{
            fontFamily: "var(--font-mono)",
            background: "var(--color-rose-hi)",
            color: "white",
            letterSpacing: "0.1em",
            border: "none",
            padding: "8px 20px",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          RE-RUN SCRAPES
        </button>
      </div>
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mt-5">
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-2"
        style={{ color: "var(--color-text-dim)", fontFamily: "var(--font-mono)" }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  wide,
  children,
}: {
  label: string;
  required?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={`flex flex-col gap-1 ${wide ? "sm:col-span-3" : ""}`}>
      <label
        className="text-[10px] font-semibold uppercase tracking-[0.18em]"
        style={{ color: "var(--color-text-dim)" }}
      >
        {label}{required && <span style={{ color: "var(--color-rose-hi)" }}> *</span>}
      </label>
      {children}
    </div>
  );
}
